/**
 * Traps.
 *
 * Content, not code. What a trap costs and how long it takes to reset are the
 * numbers the tuning conversation is about; where they go is the dungeon's
 * business.
 *
 * Deliberately visible in the world rather than hidden until stepped on. This
 * game does not punish inattention anywhere else — crops stall rather than
 * wither, a fish beyond your tackle simply never bites — and a tile that
 * removes a third of your health for walking down a corridor you had no way to
 * read would be the first thing that does. Visible, it is a decision: go round,
 * or take the hit because the way round is long.
 */

export type TrapId = 'spikes' | 'darts';

export interface TrapDefinition {
  readonly id: TrapId;
  readonly name: string;
  readonly damage: number;
  /** Seconds before it can catch you again, so a corridor stays crossable. */
  readonly rearmSeconds: number;
}

export const TRAPS: Readonly<Record<TrapId, TrapDefinition>> = {
  // A quarter of a full health bar: enough to make you look where you walk,
  // not enough to end a run on its own.
  spikes: { id: 'spikes', name: 'Spikes', damage: 5, rearmSeconds: 2.5 },
  darts: { id: 'darts', name: 'Dart Trap', damage: 9, rearmSeconds: 3.5 },
};

/** Darts appear once the shallows are behind you, alongside the better loot. */
export function trapTableFor(depth: number): readonly TrapId[] {
  return depth >= 5 ? ['spikes', 'darts', 'darts'] : ['spikes'];
}

/**
 * Roughly one trap per this many corridor tiles.
 *
 * Density rather than a count: a floor with long corridors earns more traps
 * than one that is mostly rooms, which is the difference between a threat and
 * a quota.
 */
export function trapDensityFor(depth: number): number {
  return depth >= 5 ? 14 : 22;
}
